import { useState } from 'react';

type Tab = 'dashboard' | 'templates' | 'jobs' | 'registry' | 'deploy';

type TrainingTemplate = {
  id: string;
  name: string;
  description: string;
  icon: string;
  baseModel: string;
  method: string;
  color: string;
};

type TrainingJob = {
  id: string;
  model: string;
  template: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  progress: number;
  createdAt: string;
  epochs: number;
  loss?: number;
};

type RegistryModel = {
  id: string;
  name: string;
  family: string;
  format: string;
  size: string;
  parameters: string;
  uploaded: string;
};

const TEMPLATES: TrainingTemplate[] = [
  {
    id: 'base',
    name: 'Base Model',
    description: 'Generic language model fine-tuning from any HF base',
    icon: '⊞',
    baseModel: 'Qwen2.5-0.5B',
    method: 'LoRA',
    color: '#D6A94E',
  },
  {
    id: 'code',
    name: 'Code Model',
    description: 'Code-specialized fine-tuning with programming datasets',
    icon: '⌘',
    baseModel: 'Qwen2.5-Coder-7B',
    method: 'DoRA',
    color: '#22c55e',
  },
  {
    id: 'agent',
    name: 'Agent Model',
    description: 'Tool-use and function calling optimization',
    icon: '⚙',
    baseModel: 'Qwen2.5-7B',
    method: 'LoRA',
    color: '#8b5cf6',
  },
  {
    id: 'vision',
    name: 'Vision Model',
    description: 'Multimodal vision-language fine-tuning',
    icon: '◈',
    baseModel: 'Qwen2.5-VL-7B',
    method: 'Full',
    color: '#3b82f6',
  },
  {
    id: 'voice',
    name: 'Voice Model',
    description: 'Speech and audio model fine-tuning',
    icon: '♢',
    baseModel: 'Whisper-Large-v3',
    method: 'LoRA',
    color: '#ec4899',
  },
  {
    id: 'reasoning',
    name: 'Reasoning Model',
    description: 'Chain-of-thought and reasoning enhancement',
    icon: '◆',
    baseModel: 'Qwen3-30B-A3B',
    method: 'DoRA',
    color: '#f59e0b',
  },
];

const JOBS: TrainingJob[] = [
  {
    id: 'train-fed48538',
    model: 'aether-code-v2',
    template: 'code',
    status: 'completed',
    progress: 100,
    createdAt: '2026-06-08',
    epochs: 3,
    loss: 0.024,
  },
  {
    id: 'train-9ee2d014',
    model: 'aether-agent-v1',
    template: 'agent',
    status: 'completed',
    progress: 100,
    createdAt: '2026-06-07',
    epochs: 5,
    loss: 0.042,
  },
  {
    id: 'train-active-001',
    model: 'aether-reason-v1',
    template: 'reasoning',
    status: 'running',
    progress: 62,
    createdAt: '2026-06-10',
    epochs: 10,
  },
  {
    id: 'train-queued-001',
    model: 'aether-vision-v2',
    template: 'vision',
    status: 'queued',
    progress: 0,
    createdAt: '2026-06-10',
    epochs: 8,
  },
];

const REGISTRY: RegistryModel[] = [
  {
    id: 'aether-mini-v1',
    name: 'Aether Mini v1',
    family: 'Qwen2.5-0.5B',
    format: 'GGUF Q4_K_M',
    size: '379 MB',
    parameters: '494M',
    uploaded: '2026-06-01',
  },
  {
    id: 'aether-orion-v1',
    name: 'Aether Orion v1',
    family: 'Qwen2.5-Coder-7B',
    format: 'GGUF Q4_K_M',
    size: '4.36 GB',
    parameters: '7.6B',
    uploaded: '2026-06-01',
  },
  {
    id: 'aether-code-v2',
    name: 'Aether Code v2',
    family: 'DeepSeek-Coder-33B',
    format: 'MLX',
    size: '16.2 GB',
    parameters: '33B',
    uploaded: '2026-06-08',
  },
  {
    id: 'aether-agent-v1',
    name: 'Aether Agent v1',
    family: 'Qwen2.5-7B',
    format: 'MLX',
    size: '3.8 GB',
    parameters: '7B',
    uploaded: '2026-06-07',
  },
];

const s = {
  flexCenter: { display: 'flex' as const, alignItems: 'center' as const },
  card: {
    background: 'rgba(255,255,255,0.02)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: 12,
  },
  input: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 8,
    padding: '10px 14px',
    color: '#f5f5f5',
    fontSize: 13,
    fontFamily: "'Inter', sans-serif",
    width: '100%',
    outline: 'none',
  },
};

function StatCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub?: string;
  color?: string;
}) {
  return (
    <div style={{ ...s.card, padding: '20px 24px', flex: 1, minWidth: 160 }}>
      <div
        style={{
          fontSize: 11,
          color: 'rgba(255,255,255,0.35)',
          letterSpacing: '1px',
          textTransform: 'uppercase',
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          fontFamily: "'Rajdhani', sans-serif",
          color: color || '#D6A94E',
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: TrainingJob['status'] }) {
  const colors: Record<string, string> = {
    completed: '#22c55e',
    running: '#3b82f6',
    failed: '#ef4444',
    queued: '#a3a3a3',
  };
  return (
    <span
      style={{
        fontSize: 11,
        padding: '3px 10px',
        borderRadius: 6,
        background: `${colors[status]}15`,
        color: colors[status],
        fontWeight: 600,
        letterSpacing: '0.3px',
      }}
    >
      {status.toUpperCase()}
    </span>
  );
}

export default function App() {
  const [tab, setTab] = useState<Tab>('dashboard');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState(false);

  const navItems: { id: Tab; label: string; icon: string }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: '⊞' },
    { id: 'templates', label: 'Templates', icon: '◰' },
    { id: 'jobs', label: 'Training Jobs', icon: '⚙' },
    { id: 'registry', label: 'Model Registry', icon: '◫' },
    { id: 'deploy', label: 'Deployments', icon: '⇧' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        background: '#050505',
        color: '#f5f5f5',
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <aside
        style={{
          width: 240,
          background: '#0a0a0a',
          borderRight: '1px solid rgba(255,255,255,0.04)',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
        }}
      >
        <div
          style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
        >
          <div style={{ ...s.flexCenter, gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                background: 'linear-gradient(135deg,#D6A94E,#FF6A1A)',
                ...s.flexCenter,
                justifyContent: 'center',
                fontSize: 18,
                fontWeight: 700,
                color: '#050505',
              }}
            >
              F
            </div>
            <div>
              <div
                style={{
                  fontFamily: "'Rajdhani', sans-serif",
                  fontSize: 18,
                  fontWeight: 700,
                  letterSpacing: '1px',
                  lineHeight: 1.2,
                }}
              >
                Aether Forge
              </div>
              <div
                style={{
                  fontSize: 10,
                  color: 'rgba(255,255,255,0.3)',
                  letterSpacing: '1.5px',
                  textTransform: 'uppercase',
                }}
              >
                Model Studio
              </div>
            </div>
          </div>
        </div>
        <nav style={{ flex: 1, padding: '12px 10px' }}>
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setTab(item.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                width: '100%',
                padding: '10px 14px',
                borderRadius: 8,
                cursor: 'pointer',
                border: 'none',
                fontSize: 13,
                fontWeight: 500,
                background: tab === item.id ? 'rgba(214,169,78,0.08)' : 'none',
                color: tab === item.id ? '#D6A94E' : 'rgba(255,255,255,0.5)',
                fontFamily: "'Inter', sans-serif', transition: 'all 0.15s",
                textAlign: 'left',
              }}
            >
              <span style={{ fontSize: 16, width: 20, textAlign: 'center' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </nav>
        <div style={{ padding: '16px 20px', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
          <a
            href="https://aether-models.nebulahq.work"
            style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)', textDecoration: 'none' }}
          >
            Aether Models →
          </a>
        </div>
      </aside>

      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          overflow: 'hidden',
        }}
      >
        <header
          style={{
            ...s.flexCenter,
            justifyContent: 'space-between',
            padding: '14px 28px',
            borderBottom: '1px solid rgba(255,255,255,0.04)',
            background: 'rgba(5,5,5,0.8)',
            backdropFilter: 'blur(12px)',
            flexShrink: 0,
          }}
        >
          <div style={{ ...s.flexCenter, gap: 12 }}>
            <h1
              style={{
                fontFamily: "'Rajdhani', sans-serif",
                fontSize: 20,
                fontWeight: 700,
                letterSpacing: '0.5px',
              }}
            >
              {navItems.find((n) => n.id === tab)?.label}
            </h1>
          </div>
          <div style={{ ...s.flexCenter, gap: 10 }}>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', letterSpacing: '0.5px' }}>
              {JOBS.filter((j) => j.status === 'running').length} active jobs
            </span>
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#22c55e',
                boxShadow: '0 0 6px rgba(34,197,94,0.5)',
              }}
            />
          </div>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: '28px' }}>
          {tab === 'dashboard' && (
            <div style={{ maxWidth: 960 }}>
              <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
                <StatCard
                  label="Models Trained"
                  value={String(JOBS.filter((j) => j.status === 'completed').length)}
                  sub="Total completed"
                  color="#22c55e"
                />
                <StatCard
                  label="Active Jobs"
                  value={String(JOBS.filter((j) => j.status === 'running').length)}
                  sub="Currently training"
                  color="#3b82f6"
                />
                <StatCard
                  label="Registry"
                  value={String(REGISTRY.length)}
                  sub="Models available"
                  color="#D6A94E"
                />
                <StatCard label="Deployments" value="2" sub="Active endpoints" color="#8b5cf6" />
              </div>

              <div style={{ ...s.card, padding: 24, marginBottom: 24 }}>
                <h2
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 16,
                    color: '#D6A94E',
                  }}
                >
                  Recent Training Jobs
                </h2>
                {JOBS.map((job) => (
                  <div
                    key={job.id}
                    style={{
                      ...s.flexCenter,
                      gap: 16,
                      padding: '10px 0',
                      borderBottom: '1px solid rgba(255,255,255,0.03)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{job.model}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>
                        {job.id} · {job.epochs} epochs · {job.createdAt}
                      </div>
                    </div>
                    <div style={{ width: 120 }}>
                      <div
                        style={{
                          height: 4,
                          background: 'rgba(255,255,255,0.06)',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${job.progress}%`,
                            background:
                              job.status === 'completed'
                                ? '#22c55e'
                                : job.status === 'running'
                                  ? '#3b82f6'
                                  : '#a3a3a3',
                            borderRadius: 2,
                            transition: 'width 0.5s',
                          }}
                        />
                      </div>
                      <div
                        style={{
                          fontSize: 10,
                          color: 'rgba(255,255,255,0.3)',
                          marginTop: 3,
                          textAlign: 'right',
                        }}
                      >
                        {job.progress}%
                      </div>
                    </div>
                    <StatusBadge status={job.status} />
                    {job.loss && (
                      <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                        L: {job.loss}
                      </span>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ ...s.card, padding: 24 }}>
                <h2
                  style={{
                    fontFamily: "'Rajdhani', sans-serif",
                    fontSize: 16,
                    fontWeight: 600,
                    marginBottom: 16,
                    color: '#D6A94E',
                  }}
                >
                  Quick Start Templates
                </h2>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 12,
                  }}
                >
                  {TEMPLATES.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => {
                        setSelectedTemplate(t.id);
                        setShowConfig(true);
                      }}
                      style={{
                        padding: 16,
                        borderRadius: 10,
                        cursor: 'pointer',
                        border: `1px solid ${t.color}20`,
                        background: `${t.color}08`,
                        textAlign: 'left',
                        color: '#f5f5f5',
                        fontFamily: "'Inter', sans-serif",
                        transition: 'all 0.15s',
                      }}
                    >
                      <div style={{ fontSize: 24, marginBottom: 8 }}>{t.icon}</div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{t.name}</div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'rgba(255,255,255,0.4)',
                          marginTop: 4,
                          lineHeight: 1.4,
                        }}
                      >
                        {t.description}
                      </div>
                      <div style={{ ...s.flexCenter, gap: 6, marginTop: 10 }}>
                        <span
                          style={{
                            fontSize: 10,
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: `${t.color}15`,
                            color: t.color,
                          }}
                        >
                          {t.method}
                        </span>
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                          {t.baseModel}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {tab === 'templates' && (
            <div style={{ maxWidth: 960 }}>
              <div style={{ ...s.flexCenter, justifyContent: 'space-between', marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
                  Choose a fine-tuning template to start training
                </p>
                <button
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg,#D6A94E,#FF6A1A)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#050505',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  + Custom Config
                </button>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                  gap: 16,
                }}
              >
                {TEMPLATES.map((t) => (
                  <div
                    key={t.id}
                    style={{ ...s.card, padding: 24, borderLeft: `3px solid ${t.color}` }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 12 }}>{t.icon}</div>
                    <h3
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        fontSize: 18,
                        fontWeight: 700,
                      }}
                    >
                      {t.name}
                    </h3>
                    <p
                      style={{
                        fontSize: 12,
                        color: 'rgba(255,255,255,0.4)',
                        margin: '8px 0 16px',
                        lineHeight: 1.5,
                      }}
                    >
                      {t.description}
                    </p>
                    <div style={{ ...s.flexCenter, gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                      <span
                        style={{
                          fontSize: 10,
                          padding: '3px 8px',
                          borderRadius: 4,
                          background: `${t.color}15`,
                          color: t.color,
                          fontWeight: 600,
                        }}
                      >
                        {t.method}
                      </span>
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>
                        Base: {t.baseModel}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>
                      <div>LR: 1e-4 · Batch: 8 · Epochs: 3-10</div>
                      <div>Quant: Q4_K_M · Format: MLX → GGUF</div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedTemplate(t.id);
                        setShowConfig(true);
                      }}
                      style={{
                        width: '100%',
                        padding: '8px',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 8,
                        color: '#f5f5f5',
                        cursor: 'pointer',
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: "'Inter', sans-serif",
                      }}
                    >
                      Use Template
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'jobs' && (
            <div style={{ maxWidth: 960 }}>
              <div style={{ ...s.flexCenter, justifyContent: 'space-between', marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
                  Monitor and manage training runs
                </p>
                <button
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 8,
                    color: '#f5f5f5',
                    fontSize: 13,
                    cursor: 'pointer',
                    fontFamily: "'Inter', sans-serif",
                  }}
                >
                  Filter
                </button>
              </div>
              <div style={{ ...s.card, overflow: 'hidden' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1fr',
                    gap: 12,
                    padding: '12px 20px',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    fontSize: 11,
                    color: 'rgba(255,255,255,0.3)',
                    letterSpacing: '0.5px',
                    textTransform: 'uppercase',
                  }}
                >
                  <span>Model</span>
                  <span>Template</span>
                  <span>Progress</span>
                  <span>Status</span>
                  <span>Loss</span>
                </div>
                {JOBS.map((job) => (
                  <div
                    key={job.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2fr 1fr 1.5fr 1fr 1fr',
                      gap: 12,
                      padding: '14px 20px',
                      borderBottom: '1px solid rgba(255,255,255,0.03)',
                      fontSize: 13,
                      alignItems: 'center',
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{job.model}</div>
                      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{job.id}</div>
                    </div>
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                      {job.template}
                    </span>
                    <div style={{ ...s.flexCenter, gap: 10 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 4,
                          background: 'rgba(255,255,255,0.06)',
                          borderRadius: 2,
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${job.progress}%`,
                            background: job.status === 'completed' ? '#22c55e' : '#3b82f6',
                            borderRadius: 2,
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 11 }}>{job.progress}%</span>
                    </div>
                    <StatusBadge status={job.status} />
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                      {job.loss?.toFixed(4) ?? '—'}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'registry' && (
            <div style={{ maxWidth: 960 }}>
              <div style={{ ...s.flexCenter, justifyContent: 'space-between', marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
                  {REGISTRY.length} models registered
                </p>
                <input placeholder="Search registry..." style={s.input} />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                  gap: 16,
                }}
              >
                {REGISTRY.map((m) => (
                  <div key={m.id} style={{ ...s.card, padding: 20 }}>
                    <div style={{ ...s.flexCenter, gap: 10, marginBottom: 12 }}>
                      <div
                        style={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          background: '#D6A94E',
                          boxShadow: '0 0 8px rgba(214,169,78,0.3)',
                        }}
                      />
                      <div
                        style={{
                          fontFamily: "'Rajdhani', sans-serif",
                          fontSize: 16,
                          fontWeight: 700,
                        }}
                      >
                        {m.name}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
                      <div>
                        Family: <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.family}</span>
                      </div>
                      <div>
                        Format: <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.format}</span>
                      </div>
                      <div>
                        Size: <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.size}</span>
                      </div>
                      <div>
                        Params:{' '}
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.parameters}</span>
                      </div>
                      <div>
                        Uploaded:{' '}
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>{m.uploaded}</span>
                      </div>
                    </div>
                    <div style={{ ...s.flexCenter, gap: 8, marginTop: 14 }}>
                      <button
                        style={{
                          flex: 1,
                          padding: '6px',
                          background: 'rgba(214,169,78,0.1)',
                          border: '1px solid rgba(214,169,78,0.2)',
                          borderRadius: 6,
                          color: '#D6A94E',
                          cursor: 'pointer',
                          fontSize: 11,
                          fontWeight: 600,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        Deploy
                      </button>
                      <button
                        style={{
                          flex: 1,
                          padding: '6px',
                          background: 'rgba(255,255,255,0.03)',
                          border: '1px solid rgba(255,255,255,0.06)',
                          borderRadius: 6,
                          color: 'rgba(255,255,255,0.5)',
                          cursor: 'pointer',
                          fontSize: 11,
                          fontFamily: "'Inter', sans-serif",
                        }}
                      >
                        Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'deploy' && (
            <div style={{ maxWidth: 960 }}>
              <div style={{ ...s.flexCenter, justifyContent: 'space-between', marginBottom: 20 }}>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)' }}>
                  Deploy trained models to inference endpoints
                </p>
                <button
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg,#D6A94E,#FF6A1A)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#050505',
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  + New Deployment
                </button>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: 16,
                }}
              >
                {['Aether LLM Host', 'Ollama', 'vLLM'].map((target) => (
                  <div key={target} style={{ ...s.card, padding: 20 }}>
                    <div style={{ ...s.flexCenter, gap: 10, marginBottom: 16 }}>
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          background: 'rgba(214,169,78,0.1)',
                          ...s.flexCenter,
                          justifyContent: 'center',
                          fontSize: 16,
                        }}
                      >
                        ⇧
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{target}</div>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>
                          Inference Target
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
                      <div>
                        Status: <span style={{ color: '#22c55e' }}>Available</span>
                      </div>
                      <div>
                        Models:{' '}
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                          {target === 'Aether LLM Host' ? '3 deployed' : '0 deployed'}
                        </span>
                      </div>
                      <div>
                        Endpoint:{' '}
                        <span style={{ color: 'rgba(255,255,255,0.6)' }}>
                          {target === 'Aether LLM Host' ? 'localhost:8199' : 'not configured'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {showConfig && selectedTemplate && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 100,
            ...s.flexCenter,
            justifyContent: 'center',
          }}
          onClick={() => setShowConfig(false)}
        >
          <div
            style={{
              background: '#0a0a0a',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 16,
              width: 520,
              maxHeight: '80vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '24px 28px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 20, fontWeight: 700 }}>
                Start Training
              </h2>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
                {TEMPLATES.find((t) => t.id === selectedTemplate)?.name} template
              </p>
            </div>
            <div style={{ padding: '24px 28px' }}>
              {[
                { label: 'Model Name', desc: 'Unique identifier for your model', type: 'text' },
                { label: 'Base Model', desc: 'HuggingFace model ID', type: 'text' },
                { label: 'Learning Rate', desc: '1e-4 recommended for LoRA', type: 'text' },
                { label: 'Epochs', desc: '3-10 depending on dataset size', type: 'number' },
                { label: 'Batch Size', desc: 'Per-device batch size', type: 'number' },
                { label: 'Dataset', desc: 'HuggingFace dataset path or local file', type: 'text' },
              ].map((field) => (
                <div key={field.label} style={{ marginBottom: 16 }}>
                  <label
                    style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}
                  >
                    {field.label}
                  </label>
                  <input type={field.type} placeholder={field.desc} style={s.input} />
                </div>
              ))}
            </div>
            <div
              style={{
                padding: '16px 28px',
                borderTop: '1px solid rgba(255,255,255,0.04)',
                ...s.flexCenter,
                justifyContent: 'flex-end',
                gap: 10,
              }}
            >
              <button
                onClick={() => setShowConfig(false)}
                style={{
                  padding: '8px 20px',
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: 8,
                  color: '#f5f5f5',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Cancel
              </button>
              <button
                style={{
                  padding: '8px 20px',
                  background: 'linear-gradient(135deg,#D6A94E,#FF6A1A)',
                  border: 'none',
                  borderRadius: 8,
                  color: '#050505',
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: 'pointer',
                  fontFamily: "'Inter', sans-serif",
                }}
              >
                Start Training
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
